import { AlertTriangle } from 'lucide-react';
import type { CropLoadResult } from '../../types/orchard';
import { fmt } from '../../utils/cropLoad';

const range = (r: [number, number] | null | undefined, dp: number) =>
  r ? `${fmt(r[0], dp)}–${fmt(r[1], dp)}` : null;

function Stat({ label, value, sub }: { label: string; value: string; sub?: string | null }) {
  return (
    <div className="border border-neutral-200 rounded-lg p-3 min-w-0">
      <p className="text-xs text-neutral-500">{label}</p>
      <p className="text-lg font-semibold text-neutral-900 break-words">{value}</p>
      {sub && <p className="text-xs text-neutral-500 break-words">{sub}</p>}
    </div>
  );
}

/**
 * What a crop load count works out to: per tree, for the block, and the 95 %
 * range on the trees counted. Shared by the count page's live preview and the
 * saved counts on the cultivar area.
 */
export default function CropLoadResultView({ result, showWarnings = true }: { result: CropLoadResult; showWarnings?: boolean }) {
  const r = result;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Fruit a tree" value={fmt(r.fruit_per_tree, 0)}
          sub={range(r.range?.fruit_per_tree, 0) ? `95 %: ${range(r.range?.fruit_per_tree, 0)}` : null} />
        <Stat label="Bins" value={fmt(r.bins, 0)}
          sub={range(r.range?.bins, 0) ? `95 %: ${range(r.range?.bins, 0)}` : null} />
        <Stat label="Tonnes" value={fmt(r.tonnes, 1)}
          sub={r.tonnes_per_ha != null ? `${fmt(r.tonnes_per_ha, 1)} t/ha` : null} />
        <Stat label="Fruit in the area" value={fmt(r.total_fruit)}
          sub={r.tree_count ? `${fmt(r.tree_count)} trees${r.loss_pct ? `, less ${fmt(r.loss_pct, 0)} %` : ''}` : null} />
      </div>
      <p className="text-xs text-neutral-500">
        {r.samples_counted} {r.samples_counted === 1 ? 'tree' : 'trees'} counted ({fmt(r.fruit_counted)} fruit),
        {' '}{r.trunks_measured} {r.trunks_measured === 1 ? 'trunk' : 'trunks'} measured.
        {r.fruit_per_cm2 != null && <> {fmt(r.fruit_per_cm2, 2)} fruit per cm² of cross-section</>}
        {r.mean_tcsa_cm2 != null && <> on an average trunk of {fmt(r.mean_tcsa_cm2, 1)} cm².</>}
      </p>
      {showWarnings && r.warnings.length > 0 && (
        <ul className="space-y-1">
          {r.warnings.map((w) => (
            <li key={w} className="flex items-start gap-2 text-sm text-amber-800">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{w}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
